import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import { useContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { setIsAdvancedSpacingModelOpen } from '../../../../grid/reduxStore/blockStyleSettingsSlice';
import type { RootState } from '../../../../grid/reduxStore/Store';
import { useAppDispatch } from '../../../../grid/reduxStore/Store';
import { SelectionContext } from '../../../../GridDndEditor/SelectedBlockInfoProvider';
import { useCloseAdvancedSpacingOnDeselect } from './useCloseAdvancedSpacingOnDeselect';
import { useBlockStyleSettings } from '../../../../hooks/useBlockSettings';
import { SecondaryMenu, SecondaryMenuItemTitle, SecondaryMenuSectionTitle } from '../../../component';
import { trimLeadingZeros } from '../../../../helpers/trimLeadingZeros';
const PaddingInput = ({
  label,
  name,
  value,
  onChange,
}: {
  label: string;
  name: string;
  value: number | undefined;
  onChange: (name: string, value: number) => void;
}) => {
  const [localValue, setLocalValue] = useState(value?.toString() ?? '');

  useEffect(() => {
    setLocalValue(value?.toString() ?? '');
  }, [value]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const rawValue = e.target.value;
    setLocalValue(rawValue);
  };

  const handleBlur = () => {
    const trimmed = trimLeadingZeros(localValue);
    const num = parseFloat(trimmed) || 0;
    const finalNum = Math.max(0, num);
    setLocalValue(finalNum.toString());
    onChange(name, finalNum);
  };

  return (
    <Grid item xs={6}>
      <TextField
        label={label}
        name={name}
        value={localValue}
        onChange={handleChange}
        onBlur={handleBlur}
        type="number"
        color="secondary"
        InputProps={{ inputProps: { min: 0 } }}
        variant="outlined"
        fullWidth
        sx={{
          // mb: 1,
          '& input[type=number]::-webkit-inner-spin-button, & input[type=number]::-webkit-outer-spin-button': {
            WebkitAppearance: 'reset', // Hide the up and down arrows
            margin: 0,
          },
        }}
        InputLabelProps={{
          shrink: true,
        }}
      />
    </Grid>
  );
};

export function AdvancedSpacing() {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { changePadding } = useBlockStyleSettings();
  const { selectedBlockIdByWrapper } = useContext(SelectionContext);
  const blockId = selectedBlockIdByWrapper as string;
  const { activeSidePanel } = useSelector((state: RootState) => state.blockStyleSettings);
  const {
    paddingTop = 0,
    paddingBottom = 0,
    paddingLeft = 0,
    paddingRight = 0,
  } = useSelector((state: RootState) => state.blockStyleSettings.blockStyle[blockId]) || {};
  const typeBeforeAdvancedSpacingTrigged = useRef(activeSidePanel);

  useCloseAdvancedSpacingOnDeselect(selectedBlockIdByWrapper);

  const handlePaddingChange = (name: string, value: number) => {
    changePadding(blockId, {
      paddingTop,
      paddingBottom,
      paddingRight,
      paddingLeft,
      [name]: value,
    });
  };

  const onBackButtonOnClick = () => {
    dispatch(setIsAdvancedSpacingModelOpen(false));
  };

  useEffect(() => {
    if (activeSidePanel) {
      typeBeforeAdvancedSpacingTrigged.current = activeSidePanel;
    }
  }, [activeSidePanel]);

  return (
    <SecondaryMenu
      menuTitle={t('document.grid.text.advanced_spacing.title')}
      backButtonOnClick={onBackButtonOnClick}
      testId={'advanced-spacing-component'}
    >
      <Stack rowGap="16px" width="100%">
        <SecondaryMenuSectionTitle hasUnderLine={true}>{t('document.grid.text.advanced_spacing.spacing')}</SecondaryMenuSectionTitle>
        <div>
          <SecondaryMenuItemTitle>{t('document.grid.text.advanced_spacing.padding')}</SecondaryMenuItemTitle>
          <Box sx={{ justifyContent: 'space-between', marginTop: '16px' }}>
            <Grid container spacing={2} sx={{ justifyContent: 'space-between' }}>
              <PaddingInput label="Top" name="paddingTop" value={paddingTop} onChange={handlePaddingChange} />

              <PaddingInput label="Bottom" name="paddingBottom" value={paddingBottom} onChange={handlePaddingChange} />

              <PaddingInput label="Left" name="paddingLeft" value={paddingLeft} onChange={handlePaddingChange} />

              <PaddingInput label="Right" name="paddingRight" value={paddingRight} onChange={handlePaddingChange} />
            </Grid>
          </Box>
        </div>
      </Stack>
    </SecondaryMenu>
  );
}
